'use server';

import { SearchFilters } from "@/components/job-search/search-results";
import { countDocuments, searchDocuments, updateDocument } from "@/lib/elasticsearch";
import { visaMapping } from "@/lib/visa-data";
import JOBS from '@/lib/jobs.json';
import LANGUAGE_LEVEL from "@/lib/language_level.json";
import { unstable_cache } from "next/cache";
import { generateJobFilter, JOB_MATCHING_KEYWORDS } from "@/lib/job-filter-util";
import { capitalize, generateBulletJobCrawl } from "@/lib/utils";
import stableStringify from 'json-stable-stringify';
import { removeUnicode, removeUnicodeLower } from "@/lib/string-utils";
const CANDIDATES_INDEX = 'hellojobv5-job-crawled';

export async function createSearchQuery(filter: SearchFilters, sortOption: string | null) {

    const {
        q, visaDetail, career, workLocation, job, interviewLocation, numberRecruits, netFee, netFeeNoTicket, interviewRounds, interviewDate, interviewDateType,
        basicSalary, realSalary, hourlySalary, annualIncome, annualBonus, gender, experienceRequirement, yearsOfExperience,
        age, height, weight, visionRequirement, tattooRequirement, languageRequirement, educationRequirement, dominantHand,
        otherSkillRequirement, specialConditions, companyArrivalTime, workShift, englishRequirement, suggestionType, showExpired, sortExpiredToEnd,
        hasForm
    } = filter;
    const searchQuery: any = {
        query: {
            bool: {
                filter: [
                    {
                        range: {
                            createdDate: {
                                gte: 1762502844446,
                            },
                        },
                    },
                    {
                        exists: {
                            field: "visa",
                        },
                    },
                    {
                        exists: {
                            field: "aiContent",
                        },
                    },
                    {
                        exists: {
                            field: "job",
                        },
                    },
                    {
                        exists: {
                            field: "career",
                        },
                    },
                    {
                        term: {
                            "country.keyword": "Nhật Bản",
                        },
                    },
                    {
                        exists: {
                            field: "createdDate",
                        },
                    },
                    {
                        bool: {
                            "must_not": [
                                { "term": { "isClosed": true } }
                            ]
                        }
                    }
                ],
                must: [],
                should: [],
            },
        }
    };

    if (!!q && q.length > 0) {
        const condition: any[] = [];
        const arr = q.split(';') ?? [];
        for (let i = 0; i < arr.length; i++) {
            const keyword = arr[i].toLowerCase().trim();
            if (!!JOB_MATCHING_KEYWORDS[keyword]) {
                const subConditions = [];
                for (let subIndex = 0; subIndex < JOB_MATCHING_KEYWORDS[keyword].length; subIndex++) {
                    const subKeyword = JOB_MATCHING_KEYWORDS[keyword][subIndex];
                    subConditions.push(
                        {
                            match_phrase: {
                                "aiContent": {
                                    "query": subKeyword.trim()
                                }
                            }
                        },
                        {
                            match_phrase: {
                                "baseContent": {
                                    "query": subKeyword.trim()
                                }
                            }
                        },
                        {
                            match_phrase: {
                                "matchingContent": {
                                    "query": subKeyword.trim()
                                }
                            }
                        }, {
                        match_phrase: {
                            "formMarkdownArray.noiDung": {
                                "query": subKeyword.trim()
                            }
                        }
                    });
                }
                condition.push({
                    bool: {
                        should: subConditions,
                        minimum_should_match: 1
                    }
                });
            }
            condition.push({
                bool: {
                    should: [
                        {
                            match_phrase: {
                                "aiContent": {
                                    "query": keyword.trim()
                                }
                            }
                        },
                        {
                            match_phrase: {
                                "baseContent": {
                                    "query": keyword.trim()
                                }
                            }
                        },
                        {
                            match_phrase: {
                                "matchingContent": {
                                    "query": keyword.trim()
                                }
                            }
                        },
                        {
                            match_phrase: {
                                "formMarkdownArray.noiDung": {
                                    "query": keyword.trim()
                                }
                            }
                        }
                    ],
                    minimum_should_match: 1,
                    boost: 100
                }
            });
        }

        searchQuery.query.bool.must.push({
            bool: {
                should: condition,
                minimum_should_match: 1,
            }
        });
        const now = Date.now()
        const day = 24 * 60 * 60 * 1000;
        const twelveHoursAgo = now - 0.5 * day;
        const oneDaysAgo = now - 1 * day;
        const twoDaysAgo = now - 2 * day;
        const threeDaysAgo = now - 3 * day;
        const sevenDaysAgo = now - 7 * day;
        searchQuery.query.bool.must.push({
            bool: {
                should: [{
                    range: {
                        "postedDate": {
                            "gte": Math.round(twelveHoursAgo / 1000), "boost": 70
                        }
                    }
                }, {
                    range: {
                        "postedDate": {
                            "gte": Math.round(oneDaysAgo / 1000), "boost": 60
                        }
                    }
                }, {
                    range: {
                        "postedDate": {
                            "gte": Math.round(twoDaysAgo / 1000), "boost": 50
                        }
                    }
                }, {
                    range: {
                        "postedDate": {
                            "gte": Math.round(threeDaysAgo / 1000), "boost": 40
                        }
                    }
                }, {
                    range: {
                        "postedDate": {
                            "gte": Math.round(sevenDaysAgo / 1000), "boost": 30
                        }
                    }
                }
                ],
                minimum_should_match: 1,
            }
        });
        // searchQuery.query.bool.filter.push({
        //     "range": {
        //         "expiredDate": {
        //             "gte": Date.now(),
        //         }
        //     }
        // });
        const sort: any[] = [];

        if (showExpired) {
            if (sortExpiredToEnd) {
                sort.unshift({
                    "_script": {
                        "type": "number",
                        "order": "asc",
                        "script": {
                            "source": `
            def now = new Date().getTime();
            if (doc['expiredDate'].size() == 0) return 0;
            long exp = doc['expiredDate'].value;
            return now > exp ? 1 : 0;
          `
                        }
                    }
                });
            }
        } else {
            searchQuery.query.bool.filter.push({
                "range": {
                    "expiredDate": {
                        "gte": Date.now(),
                    }
                }
            });
        }
        searchQuery.sort = sort;
        // sort.push({ "_score": { "order": "desc" } });
        // sort.push({ "postedDate": { "order": "desc" } });
        // searchQuery.sort = sort;
    } else {
        const sort: any[] = [];
        switch (sortOption) {
            case 'salary_desc': {
                sort.push({
                    "_script": {
                        "type": "number",
                        "order": "desc",
                        "script": {
                            "source": `
        if (doc['basicSalary'].size() == 0 || doc['basicSalary'].value == 0) {
          return -1;
        }
        return doc['basicSalary'].value;
      `
                        }
                    }
                })
                break;
            }
            case 'salary_asc': {
                sort.push({
                    "_script": {
                        "type": "number",
                        "order": "asc",
                        "script": {
                            "source": `
        if (doc['basicSalary'].size() == 0 || doc['basicSalary'].value == 0) {
          return 99999999;
        }
        return doc['basicSalary'].value;
      `
                        }
                    }
                })
                break;
            }
            case 'net_salary_desc': {
                sort.push({
                    "_script": {
                        "type": "number",
                        "order": "desc",
                        "script": {
                            "source": `
        if (doc['realSalary'].size() == 0 || doc['realSalary'].value == 0) {
          return -1;
        }
        return doc['realSalary'].value;
      `
                        }
                    }
                })
                break;
            }
            case 'net_salary_asc': {
                sort.push({
                    "_script": {
                        "type": "number",
                        "order": "asc",
                        "script": {
                            "source": `
        if (doc['realSalary'].size() == 0 || doc['realSalary'].value == 0 || doc['fee'].value >20000 || doc['fee'].value <100) {
          return 99999999;
        }
        return doc['realSalary'].value;
      `
                        }
                    }
                })
                break;
            }
            case 'fee_desc': {
                sort.push({
                    "_script": {
                        "type": "number",
                        "order": "desc",
                        "script": {
                            "source": `
        if (doc['fee'].size() == 0 || doc['fee'].value == 0 || doc['fee'].value >20000 || doc['fee'].value <100) {
          return -1;
        }
        return doc['fee'].value;
      `
                        }
                    }
                })
                break;
            }
            case 'fee_asc': {
                sort.push({
                    "_script": {
                        "type": "number",
                        "order": "asc",
                        "script": {
                            "source": `
        if (doc['fee'].size() == 0 || doc['fee'].value == 0) {
          return 99999999;
        }
        return doc['fee'].value;
      `
                        }
                    }
                })
                break;
            }
            case 'interview_date_asc': {
                sort.push({
                    "interviewDay": {
                        "order": "asc",
                        "missing": "_last"
                    }
                });
                break;
            }
            case 'interview_date_desc': {
                sort.push({
                    "interviewDay": {
                        "order": "desc",
                        "missing": "_last"
                    }
                });
                break;
            }
            case 'newest': {
                sort.push({ "postedDate": { "order": "desc" } });
                break;
            }
            default: {
                sort.push({ "_score": { "order": "desc" } });
                sort.push({ "postedDate": { "order": "desc" } });
                break;
            }
        }
        if (showExpired) {
            if (sortExpiredToEnd) {
                sort.unshift({
                    "_script": {
                        "type": "number",
                        "order": "asc",
                        "script": {
                            "source": `
            def now = new Date().getTime();
            if (doc['expiredDate'].size() == 0) return 0;
            long exp = doc['expiredDate'].value;
            return now > exp ? 1 : 0;
          `
                        }
                    }
                });
            }
        } else {
            searchQuery.query.bool.filter.push({
                "range": {
                    "expiredDate": {
                        "gte": Date.now(),
                    }
                }
            });
        }
        searchQuery.sort = sort;
    }
    if (hasForm) {
        searchQuery.query.bool.filter.push(
            {
                exists: {
                    field: "formMarkdownArray",
                },
            }
        );
    }
    const conditionFilters = [];
    const conditionMust = [];
    if (!!visaDetail && visaDetail !== "all-details" && visaDetail !== "" && visaDetail !== "all") {
        const visaLabel = visaMapping[visaDetail as keyof typeof visaMapping] ?? visaDetail;
        conditionFilters.push({
            term: {
                "visa.keyword": visaLabel?.replace(', tri thức', ''),
            },
        });
    }
    if (["thực tập sinh 3 năm", "thực tập sinh 1 năm"].includes(visaDetail ?? '')) {
        conditionFilters.push({
            bool: {
                should: [
                    {
                        range: {
                            interviewDay: {
                                gte: "now",
                                format: "dd/MM/yyyy",
                            },
                        },
                    },
                    {
                        bool: {
                            must_not: {
                                exists: { field: "interviewDay" },
                            },
                        },
                    },
                ],
                minimum_should_match: 1,
            },
        });
    }
    if (!!basicSalary?.length) {
        try {
            const basicSalaryNumber = Number(basicSalary)
            if (isNaN(basicSalaryNumber)) {
                throw '';
            }
            conditionFilters.push({
                range: {
                    basicSalary: {
                        gte: basicSalaryNumber,
                    },
                },
            });
        } catch (error) {

        }
    }
    if ((!!career && career !== 'all') || (!!job && job !== 'all-details')) {
        const jobObj = JOBS.find(j => j.value === job);
        const jobCodeArr = jobObj?.valueArr;
        let should = [];
        if (jobCodeArr?.length === 5) {
            should.push({ term: { "filter.job.value.keyword": { "value": job, "boost": 5 } } });
            if (suggestionType !== 'accurate') {
                should.push({
                    prefix: {
                        "filter.job.value.keyword": { "value": `${jobCodeArr[0]}.${jobCodeArr[1]}.${jobCodeArr[2]}.${jobCodeArr[3]}.`, "boost": 3 },
                    },
                });
                should.push({
                    prefix: {
                        "filter.job.value.keyword": {
                            value: `${jobCodeArr[0]}.${jobCodeArr[1]}.${jobCodeArr[2]}.`,
                            "boost": 1
                        }
                    }
                });
            }
            // should.push({ prefix: { "filter.job.value.keyword": `${jobCodeArr[0]}.${jobCodeArr[1]}.` } });
        } else {
            if (!!job) {
                should.push({
                    term: {
                        "career.keyword": {
                            value: jobObj?.label,
                            boost: 1
                        }
                    }
                });
                should.push({
                    term: {
                        "job.keyword": {
                            value: jobObj?.label,
                            boost: 3
                        }
                    }
                });
                // should.push({ match: { career: job } });
                // should.push({ match: { job: job } });
            } else if (!!career) {
                should.push({
                    term: {
                        "career.keyword": {
                            value: career,
                            boost: 1
                        }
                    }
                });
                should.push({
                    term: {
                        "job.keyword": {
                            value: career,
                            boost: 3
                        }
                    }
                });
                // should.push({ match: { career: career } });
                // should.push({ match: { job: career } });
            }
        }
        conditionMust.push({
            bool: {
                should: should,
                minimum_should_match: 1,
            },
        });
    }

    if (!!workLocation && workLocation.length > 0) {
        // workLocation.forEach((location: string) => {
        //     const locationLower = location.toLowerCase();
        //     const province = PROVINCES.find((item) => item.label.toLowerCase() === locationLower && item.level > 0);
        //     if (!!province) {
        //         const parentCode = province.parentCode;
        //         const parentRegion = PROVINCES?.find((item) => item.value === parentCode)?.label;
        //         if (!!parentRegion && matchingLocations.indexOf(parentRegion) === -1) {
        //             matchingLocations.push(parentRegion);
        //         }
        //     }
        // });
        const shouldLocation: any = [
            {
                bool: {
                    must_not: {
                        exists: { field: "workLocation" },
                    },
                },
            },
        ];
        workLocation.forEach((location: string) => {
            if (!!location && location.trim() !== "empty") {
                shouldLocation.push({ match: { workLocation: capitalize(location.trim()) } });
            }
        });
        conditionMust.push({
            bool: {
                should: shouldLocation,
                minimum_should_match: 1,
            },
        });
    }
    if (!!specialConditions && specialConditions.length > 0) {
        conditionFilters.push({
            terms: {
                specialConditions: specialConditions,
            },
        });
    }
    if (!!gender && gender.length > 0) {
        let genderValues: string[] = [];
        let shouldClauses = [];
        if (gender === "vo-chong") {
            shouldClauses.push({
                match_phrase: {
                    "aiContent": {
                        "query": "vợ chồng",
                        slop: 2
                    }
                }
            }, {
                match_phrase: {
                    "aiContent": {
                        "query": "vo chong",
                        slop: 2
                    }
                }
            }, {
                match_phrase: {
                    "aiContent": {
                        "query": "v/c"
                    }
                }
            }, {
                match_phrase: {
                    "aiContent": {
                        "query": "vc"
                    }
                }
            }, {
                match_phrase: {
                    "aiContent": {
                        "query": "v.c"
                    }
                }
            });
        } else if (gender === "cap-doi") {
            shouldClauses.push({
                match_phrase: {
                    "aiContent": {
                        "query": "cặp đôi",
                        slop: 2
                    }
                }
            }, {
                match_phrase: {
                    "aiContent": {
                        "query": "cap doi",
                        slop: 2
                    }
                }
            }, {
                match_phrase: {
                    "aiContent": {
                        "query": "c.đôi"
                    }
                }
            }, {
                match_phrase: {
                    "aiContent": {
                        "query": "c/đôi"
                    }
                }
            });
        } else if (gender === "nam") {
            genderValues = ["MALE", "NAM", "BOTH"];
            shouldClauses.push({ terms: { "gender.keyword": genderValues } });
        } else if (gender === "nu") {
            genderValues = ["FEMALE", "NỮ", "NU", "BOTH"];
            shouldClauses.push({ terms: { "gender.keyword": genderValues } });
        }
        // shouldClauses.push({
        //     bool: { must_not: { exists: { field: "gender" } } },
        // });
        if (shouldClauses.length > 0) {
            conditionFilters.push({
                bool: {
                    should: shouldClauses,
                    minimum_should_match: 1,
                },
            });
        }
    }
    if (age && age.length === 2) {
        conditionFilters.push({
            bool: {
                should: [{
                    range: {
                        "filter.minAge": {
                            lte: age[0],
                        },
                    },
                }, {
                    bool: {
                        must_not: {
                            exists: { field: "filter.minAge" },
                        },
                    },
                }],
                minimum_should_match: 1,
            },
        });
        conditionFilters.push({
            bool: {
                should: [{
                    range: {
                        "filter.maxAge": {
                            lte: age[1],
                        },
                    },
                }, {
                    bool: {
                        must_not: {
                            exists: { field: "filter.maxAge" },
                        },
                    },
                }],
                minimum_should_match: 1,
            },
        });
    }
    if (!!languageRequirement && languageRequirement.length > 0) {
        const level = LANGUAGE_LEVEL.find(level => level.slug === languageRequirement);
        if (!!level) {
            const levels = LANGUAGE_LEVEL.filter(lv => lv.level <= level.level).map(lv => lv.name);
            conditionMust.push({
                terms: {
                    "languageLevel.keyword": levels,
                },
            });
        }
    }
    if (!!netFee && netFee.length > 0) {
        const feeNum = Number(netFee);
        conditionMust.push({
            range: {
                fee: {
                    lte: feeNum,
                    gt: 0
                },
            },
        });
    }
    Object.keys(filter).forEach((key) => {
        // Bỏ qua các trường đặc biệt đã xử lý ở trên
        if (['visa', 'visaDetail', 'workLocation', 'specialConditions', 'career', 'job', 'gender', 'age',
            'interviewDateType', 'q', 'height', 'realSalary', 'basicSalary', 'netFee', 'interviewLocation', 'suggestionType',
            'languageRequirement', 'tattooRequirement', 'weight'].includes(key)) return;

        const value = filter[key as keyof SearchFilters];
        if (value === undefined || value === null || value === '' || value === 'all' || (Array.isArray(value) && value.length === 0)) return;
        if (Array.isArray(value)) {
            conditionFilters.push({
                terms: {
                    [`filter.${key}.keyword`]: value,
                },
            });
        } else if (typeof value === 'string') {
            conditionFilters.push({
                term: {
                    [`filter.${key}.keyword`]: value,
                },
            });
        }
    });
    if (suggestionType === 'related') {
        searchQuery.query.bool.should.push(...conditionFilters);
        searchQuery.query.bool.should.push(...conditionMust);
        searchQuery.query.bool.minimum_should_match = 1;
    } else {
        searchQuery.query.bool.filter.push(...conditionFilters);
        searchQuery.query.bool.must.push(...conditionMust);
    }

    // delete searchQuery.sort
    return searchQuery;
}
async function fetchJobsFromES(filter: SearchFilters, sortOption: string | null, page: number, limit: number) {
    const query = await createSearchQuery(filter, sortOption);
    query._source = [
        "id", "code", "avatar", "job", "career", "visa", "realSalary", "basicSalary",
        "nameAscii",
        "realSalaryCode", "basicSalaryCode", "workLocation", "interviewDay",
        "postedDate", "time", "createdDate", "expiredDate", "salerID", "source",
        "sender", "formImage", "formMarkdownArray", "specialConditions", "fee",
        "numberRecruits", "minAge", "maxAge", "languageLevel", "interviewRounds",
        "interviewLocation", "educationLevel", "experience", "experienceYear",
        "height", "weight", "vgb", "haveTattoo", "vision", "interviewFormat",
        "baseContent", "aiContent", "contact", "groupLink", "postLink",
        "senderLink", "groupName", "filter", "gender"
    ];

    const results = await searchDocuments<any>(CANDIDATES_INDEX, query, page, limit);
    const mappedDocs = results.docs.map(doc => ({
        ...doc,
        id: doc.id,
        source: doc.source,
    }));
    return { ...results, docs: mappedDocs };
}
export const getJobs = async (filter: SearchFilters, sortOption: string | null, page: number, limit: number = 10) => {
    // Tạo key dựa trên các tham số tìm kiếm để cache chính xác từng loại filter
    const cacheKey = [
        'jobs-search',
        stableStringify(filter) ?? '',
        sortOption || 'none',
        page.toString(),
        limit.toString()
    ];

    return unstable_cache(
        async () => fetchJobsFromES(filter, sortOption, page, limit),
        cacheKey,
        {
            revalidate: 3600, // Cache trong 1 giờ (giây)
            tags: ['jobs-list'] // Dùng để xóa cache sau này
        }
    )();
};
export const getJobsByIDs = async (ids: string[]) => {
    if (!ids || ids.length === 0) return { docs: [], total: 0, page: 1, limit: 10, totalPages: 0 };
    return unstable_cache(
        async () => {
            const query = {
                query: { bool: { must: [{ terms: { "id.keyword": ids } }] } },
                sort: [
                    {
                        "_script": {
                            "type": "number", "order": "asc",
                            "script": { "source": "def now = new Date().getTime(); if (doc['expiredDate'].size() == 0) return 0; long exp = doc['expiredDate'].value; return now > exp ? 1 : 0;" }
                        }
                    },
                    { createdDate: { order: 'desc' } }
                ]
            };
            const results = await searchDocuments<any>(CANDIDATES_INDEX, query, 1, 10);
            return {
                ...results,
                docs: results.docs.map(doc => ({ ...doc, id: doc.id, source: doc.source }))
            };
        },
        ['jobs-by-ids', ids.sort().join(',')],
        { revalidate: 3600, tags: ['jobs-list'] }
    )();
};
export const getJobsBySalerID = async (id: string, page: number, limit: number = 10) => {
    return unstable_cache(
        async () => {
            try {
                const searchQuery: any = {
                    query: {
                        bool: {
                            filter: [
                                {
                                    range: {
                                        createdDate: {
                                            gte: 1762502844446,
                                        },
                                    },
                                },
                                {
                                    exists: {
                                        field: "visa",
                                    },
                                },
                                {
                                    exists: {
                                        field: "aiContent",
                                    },
                                },
                                {
                                    exists: {
                                        field: "job",
                                    },
                                },
                                {
                                    exists: {
                                        field: "career",
                                    },
                                },
                                {
                                    term: {
                                        "country.keyword": "Nhật Bản",
                                    },
                                },
                                {
                                    exists: {
                                        field: "createdDate",
                                    },
                                },
                            ],
                            must: [
                                { term: { "salerID.keyword": id } }
                            ],
                        },
                    },
                    sort: [
                        {
                            "_script": {
                                "type": "number",
                                "order": "asc",
                                "script": {
                                    "source": `
            def now = new Date().getTime();
            if (doc['expiredDate'].size() == 0) return 0;
            long exp = doc['expiredDate'].value;
            return now > exp ? 1 : 0;
          `
                                }
                            }
                        },
                        { createdDate: { order: 'desc' } }
                    ]
                };
                const results = await searchDocuments<any>(CANDIDATES_INDEX, searchQuery, page, limit);
                const mappedDocs: any[] = results.docs.map(doc => {
                    const name = doc.fullName || doc.sender;
                    return {
                        ...doc,
                        id: doc.id,
                        source: doc.source, // Fixed: Added back the source field
                    }
                });

                return { ...results, docs: mappedDocs };
            } catch (error: any) {
                console.error("Failed to fetch new candidates from Elasticsearch:", error);
                if (error.meta?.body?.error?.type === 'index_not_found_exception') {
                    console.log(`Index ${CANDIDATES_INDEX} not found. Returning empty results.`);
                }
                return { docs: [], total: 0, page: 1, limit: 10, totalPages: 0 };
            }
        },
        ['jobs-saler', id, page.toString()],
        { revalidate: 3600, tags: [`saler-${id}`] }
    )();
};
export const countJobs = async (filter: SearchFilters) => {
    return unstable_cache(
        async () => {
            const query = await createSearchQuery(filter, null);
            delete query.sort;
            return await countDocuments(CANDIDATES_INDEX, query);
        },
        ['jobs-count', stableStringify(filter) ?? ''],
        { revalidate: 3600, tags: ['jobs-list'] }
    )();
};
export const getHomeJobs = async (key: String, searcLink: string, limit: number = 8) => {
    return unstable_cache(
        async () => {
            searcLink = searcLink.replace('sap-xep', 'sortBy');
            const url = new URL(`${process.env.DOMAIN}${searcLink}`);
            const searchParams = url.searchParams;
            searchParams.set('showExpired', 'false');
            const { newFilters, sortOption } = generateJobFilter(url.searchParams);
            const query = await createSearchQuery(newFilters, sortOption);
            query._source = [
                "id",
                "nameAscii",          // Định danh document
                "code",             // Mã công việc hiển thị trên ảnh
                "avatar",           // Ảnh đại diện công việc
                "job",              // Tên công việc (dùng để lấy ảnh mặc định nếu ko có avatar)
                "career",           // Ngành nghề (dùng để lấy ảnh mặc định)
                "visa",             // Thông tin Visa để hiển thị Badge
                "realSalary",       // Lương thực lĩnh
                "basicSalary",             // Thông tin Visa để hiển thị Badge
                "realSalaryCode",       // Lương thực lĩnh
                "basicSalaryCode",      // Lương cơ bản
                "workLocation",     // Địa điểm làm việc
                "interviewDay",     // Ngày phỏng vấn
                "postedDate",       // Ngày đăng (convertTime)
                "time",             // Trường dự phòng cho ngày đăng
                "createdDate",      // Trường dự phòng cho ngày đăng
                "expiredDate",      // Ngày hết hạn để hiển thị trạng thái
                "salerID",          // Để tìm thông tin tư vấn viên (recruiter)
                "source",           // Nguồn gốc (ZALO, FACEBOOK,...) dùng cho Admin logic
                "sender",           // Tên người gửi (hiển thị NameAvatar)
                "formImage",        // Ảnh form đơn hàng
                "formMarkdownArray", // Dùng để generate HTML form
                "specialConditions",
                "fee",
                "numberRecruits",
                "minAge",
                "maxAge",
                "languageLevel",
                "interviewRounds",
                "interviewLocation",
                "educationLevel",
                "experience",
                "experienceYear",
                "height",
                "weight",
                "vgb",
                "haveTattoo",
                "vision",
                "interviewFormat",
                "baseContent",
                "aiContent",
                "baseContent",
                "contact",
                "groupLink",
                "postLink",
                "senderLink",
                "groupName",
                "filter"
            ];
            const now = new Date();
            now.setMinutes(0, 0, 0);
            query.query.bool.filter.push({
                "range": {
                    "expiredDate": {
                        "gte": now.getTime(),
                    }
                }
            });

            const results = await searchDocuments<any>(CANDIDATES_INDEX, query, 1, limit);
            const mappedDocs = results.docs.map(doc => ({
                ...doc,
                id: doc.id,
                source: doc.source,
            }));
            return { ...results, docs: mappedDocs };
        },
        [`home-jobs-${key}-${limit}`],
        { revalidate: 3600, tags: ['home-jobs', `home-jobs-${key}-${limit}`] }
    )();
}
// export const findAllJobs = async () => {
//     const query: any = {
//         "query": {
//             "bool": {
//                 "filter": [
//                     {
//                         "range": {
//                             "createdDate": {
//                                 "gte": 1762502844446
//                             }
//                         }
//                     },
//                     {
//                         "exists": {
//                             "field": "visa"
//                         }
//                     },
//                     {
//                         "exists": {
//                             "field": "aiContent"
//                         }
//                     },
//                     {
//                         "exists": {
//                             "field": "job"
//                         }
//                     },
//                     {
//                         "exists": {
//                             "field": "career"
//                         }
//                     },
//                     {
//                         "term": {
//                             "country.keyword": "Nhật Bản"
//                         }
//                     },
//                     {
//                         "exists": {
//                             "field": "createdDate"
//                         }
//                     },
//                     {
//                         "bool": {
//                             "must_not": [
//                                 {
//                                     "term": {
//                                         "isClosed": true
//                                     }
//                                 }
//                             ]
//                         }
//                     }
//                 ],
//                 "must": [],
//                 "should": []
//             }
//         },
//         "sort": [
//             {
//                 "id.keyword": {
//                     "order": "asc"
//                 }
//             }
//         ]
//     };
//     query._source = [
//         "id", "code", "job", "career", "visa", "realSalary", "basicSalary",
//         "realSalaryCode", "basicSalaryCode", "workLocation", "specialConditions", "fee",
//         "numberRecruits", "languageLevel", "vgb", "haveTattoo", "gender"
//     ];
//     const { docs, total } = await searchDocuments(CANDIDATES_INDEX, query, 1, 9999);
//     console.log(total)
//     docs.forEach(async (doc, index) => {
//         let bullet = doc.visa.toLowerCase().replace('tokutei','dac-dinh') + ' ' + generateBulletJobCrawl(doc);
//         bullet = bullet.replaceAll(', ', ' ').replaceAll(';', ' ').replaceAll('(', '').replaceAll(')', '').replaceAll(',', '') + ' ' + doc.code;
//         const nameAscii = removeUnicodeLower(bullet, "-");
//         await updateDocument(CANDIDATES_INDEX,doc.id, {
//             nameAscii
//         })
//         console.log("Cập nhật " + doc.id + "; " + (index + 1));
//         console.log("Bullet: " + bullet);
//         console.log("NameAscii: " + nameAscii);
//         console.log("==============================");
//     })
// }